import React from 'react';
import { Grid, Table, Statistic } from 'semantic-ui-react';
import { FormattedNumber } from 'react-intl';

import { map, mapIndexed } from 'helpers/safeRamda';
import ReportBase, { ReportParams } from 'scenes/Report/components/ReportBase';
import FormattedCurrency from 'scenes/Report/components/FormattedCurrency';
import { ClassProp } from 'helpers/types';

import ReplaceTextConfig from './RepeatVisits.batch.config';
import styles from './RepeatVisits.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;

type PurchasesBySubWithMulPurchases = {
  cashbackPercentage: number;
  totalPurchases: string;
  numberOfTransactors: number;
  percentOfMerchantTotal: number;
  aov: number;
  startDate: string;
  endDate: string;
  percentRepeat: number;
};

type PercentNewTransactors = {
  cashbackPercentage: number;
  percentRepeat: number;
};

const headerRow = [
  'Total Purchases',
  'Number of Transactors',
  '% of Total',
  'AOV',
];

const renderTableHeader = (classes: Styles) => (
  <Table.Row>
    {map((header: string) => (
      <Table.HeaderCell key={header} className={classes.tableHeader}>
        {header}
      </Table.HeaderCell>
    ))(headerRow)}
  </Table.Row>
);

const renderBodyRow = mapIndexed(
  (
    { totalPurchases, numberOfTransactors, percentOfMerchantTotal, aov }: any,
    index: number,
  ) => (
    <Table.Row key={index}>
      <Table.Cell>{totalPurchases}</Table.Cell>
      <Table.Cell>
        <FormattedNumber value={numberOfTransactors} />
      </Table.Cell>

      <Table.Cell>
        <FormattedNumber
          value={percentOfMerchantTotal / 100}
          minimumFractionDigits={1}
          style="percent"
        />
      </Table.Cell>

      <Table.Cell>
        <FormattedCurrency value={aov} />
      </Table.Cell>
    </Table.Row>
  ),
);

export const renderTable = (
  params: ReportParams,
  header: string,
  batchKey: string,
  title: string,
  segmentValue: number | string | null,
  classes: Styles,
  idx: number,
  purchasesBySubWithMulPurchases: PurchasesBySubWithMulPurchases[],
  percentNewBySegments: PercentNewTransactors[],
): JSX.Element => {
  const { percentRepeat } = percentNewBySegments[0];
  const { startDate, endDate } = purchasesBySubWithMulPurchases[0];

  return (
    <ReportBase
      batchKey={batchKey}
      header={header}
      title={title}
      params={params}
      segmentedBatchData={{
        idx,
        startDate,
        endDate,
        replaceTextConfig: ReplaceTextConfig,
        isSegmented: true,
        segmentValue,
      }}
      key={idx}
    >
      <Grid.Row className={classes.charts} centered>
        <Grid.Column
          textAlign="center"
          verticalAlign="middle"
          width={4}
          className={classes.gridTable}
        >
          <Statistic size="tiny">
            <Statistic.Value className={classes.value}>
              <FormattedNumber value={percentRepeat || 0} style="percent" />
            </Statistic.Value>

            <Statistic.Label className={classes.label}>
              Transact more than once
            </Statistic.Label>
          </Statistic>
        </Grid.Column>

        <Grid.Column
          textAlign="center"
          width={12}
          className={classes.gridTable}
        >
          <Table celled textAlign="center">
            <Table.Header>{renderTableHeader(classes)}</Table.Header>

            <Table.Body>
              {renderBodyRow(purchasesBySubWithMulPurchases || [])}
            </Table.Body>
          </Table>
        </Grid.Column>
      </Grid.Row>
    </ReportBase>
  );
};
